import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

const PAGE_PARAM = 'page'

/**
 * Хранит номер страницы пагинации в URL (?page=) вместо локального useState.
 * Drop-in замена для `const [page, setPage] = useState(1)` — благодаря этому
 * страницу можно расшарить ссылкой, она переживает back/forward и перезагрузку.
 *
 * На дефолтной странице параметр из URL убирается, чтобы ссылка оставалась чистой.
 * Прочие query-параметры (вкладки, фильтры) не затираются.
 */
export const usePageParam = (defaultPage = 1): readonly [number, (page: number) => void] => {
	const [searchParams, setSearchParams] = useSearchParams()

	const parsed = Number(searchParams.get(PAGE_PARAM))
	const page = Number.isInteger(parsed) && parsed > 0 ? parsed : defaultPage

	const setPage = useCallback(
		(next: number) => {
			setSearchParams(
				prev => {
					if (next <= defaultPage) {
						prev.delete(PAGE_PARAM)
					} else {
						prev.set(PAGE_PARAM, String(next))
					}
					return prev
				},
				{ replace: false }
			)
		},
		[setSearchParams, defaultPage]
	)

	return [page, setPage] as const
}
